import _extends from "@babel/runtime/helpers/extends";
/** @jsx jsx */
import { jsx } from '@emotion/react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import DropdownMenu from '@atlaskit/dropdown-menu';
import LozengeActionTrigger from './lozenge-action-trigger';
import LozengeActionError from './lozenge-action-error';
import withErrorBoundary from './error-boundary';
import useInvoke from '../../../../../../state/hooks/use-invoke';
import extractLozengeActionItems from '../../../../../../extractors/action/extract-lozenge-action-items';
import createStatusUpdateRequest from '../../../../../../utils/actions/create-status-update-request';
import useResolve from '../../../../../../state/hooks/use-resolve';
import { LozengeActionErrorMessages } from './lozenge-action-error/types';
import { isInvokeCustomError } from '../../../../../../state/hooks/use-invoke/utils';
import { useFlexibleUiAnalyticsContext } from '../../../../../../state/flexible-ui-context';
import { TrackQuickActionType } from '../../../../../../utils/analytics/analytics';
import { permissionLoadErrorAnalyticsPayload, unknownLoadErrorAnalyticsPayload, unknownUpdateErrorAnalyticsPayload, validationUpdateErrorAnalyticsPayload } from './lozenge-action-analytics';
import LozengeActionItemsGroup from './lozenge-action-items-group';
const validateItems = (items = [], text) => {
  return items.filter(item => item.text !== text);
};
const LozengeAction = ({
  action,
  appearance,
  testId = 'smart-element-lozenge-action',
  text,
  zIndex
}) => {
  var _action$update;
  const [selected, setSelected] = useState({
    appearance,
    text
  });
  const [isLoaded, setIsLoaded] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [items, setItems] = useState();
  const [errorMessage, setErrorMessage] = useState();
  const reload = useResolve();
  //TODO EDM-6583 Replace usage of useFlexibleUiAnalyticsContext with linking platform analytics context from find team.
  const analytics = useFlexibleUiAnalyticsContext();
  const invoke = useInvoke();
  useEffect(() => {
    setSelected({
      text,
      appearance
    });
  }, [text, appearance]);
  const {
    url,
    id: linkId,
    previewData
  } = (action === null || action === void 0 ? void 0 : (_action$update = action.update) === null || _action$update === void 0 ? void 0 : _action$update.details) || {};
  const handleOpenChange = useCallback(async args => {
    setIsOpen(args.isOpen);
    if (args.isOpen) {
      analytics === null || analytics === void 0 ? void 0 : analytics.ui.smartLinkLozengeActionClickedEvent();
      analytics === null || analytics === void 0 ? void 0 : analytics.track.smartLinkQuickActionStarted({
        smartLinkActionType: TrackQuickActionType.StatusUpdate
      });
      if (!isLoaded && action !== null && action !== void 0 && action.read) {
        try {
          setIsLoading(true);
          const responseItems = await invoke(action.read, extractLozengeActionItems);
          const validItems = typeof text === 'string' ? validateItems(responseItems, text) : responseItems;
          setItems(validItems);
          setIsLoaded(true);
          if ((validItems === null || validItems === void 0 ? void 0 : validItems.length) === 0) {
            analytics === null || analytics === void 0 ? void 0 : analytics.track.smartLinkQuickActionFailed(permissionLoadErrorAnalyticsPayload);
            setErrorMessage(LozengeActionErrorMessages.noData);
            setIsLoaded(false);
          }
        } catch (err) {
          setErrorMessage(LozengeActionErrorMessages.unknown);
          setIsLoaded(false);
          analytics === null || analytics === void 0 ? void 0 : analytics.track.smartLinkQuickActionFailed(unknownLoadErrorAnalyticsPayload);
        } finally {
          setIsLoading(false);
        }
      }
    }
    if (!args.isOpen) {
      setErrorMessage(undefined);
    }
  }, [action.read, analytics, invoke, isLoaded, text]);
  const trigger = useCallback(props => jsx(LozengeActionTrigger, _extends({}, props, {
    appearance: selected.appearance,
    isOpen: isOpen,
    testId: testId,
    text: selected.text
  })), [selected.appearance, selected.text, isOpen, testId]);
  const handleItemClick = useCallback(async (id, text, appearance) => {
    try {
      analytics === null || analytics === void 0 ? void 0 : analytics.ui.smartLinkLozengeActionListItemClickedEvent();
      const updateAction = action === null || action === void 0 ? void 0 : action.update;
      if (updateAction && id) {
        setIsLoading(true);
        const request = createStatusUpdateRequest(updateAction, id);
        await invoke(request);
        setSelected({
          appearance,
          text
        });
        setIsLoading(false);
        setIsLoaded(false);
        setIsOpen(false);
        setItems([]);
        analytics === null || analytics === void 0 ? void 0 : analytics.track.smartLinkQuickActionSuccess({
          smartLinkActionType: TrackQuickActionType.StatusUpdate
        });
        if (url) {
          await reload(url, true, undefined, linkId);
        }
      }
    } catch (err) {
      setIsLoading(false);
      if (isInvokeCustomError(err)) {
        setErrorMessage(err.message);
        analytics === null || analytics === void 0 ? void 0 : analytics.track.smartLinkQuickActionFailed(validationUpdateErrorAnalyticsPayload);
      } else {
        setErrorMessage(LozengeActionErrorMessages.updateFailed);
        analytics === null || analytics === void 0 ? void 0 : analytics.track.smartLinkQuickActionFailed(unknownUpdateErrorAnalyticsPayload);
      }
    }
  }, [action === null || action === void 0 ? void 0 : action.update, analytics, invoke, linkId, reload, url]);
  const dropdownItemGroup = useMemo(() => {
    if (errorMessage) {
      return jsx(LozengeActionError, {
        errorMessage: errorMessage,
        testId: testId,
        url: url,
        previewData: previewData
      });
    }
    if (items && items.length > 0) {
      return jsx(LozengeActionItemsGroup, {
        testId: testId,
        items: items,
        onClick: handleItemClick
      });
    }
  }, [errorMessage, handleItemClick, items, previewData, testId, url]);
  return jsx(DropdownMenu, {
    isLoading: isLoading,
    isOpen: isOpen,
    onOpenChange: handleOpenChange,
    testId: testId,
    trigger: trigger,
    zIndex: zIndex
  }, dropdownItemGroup);
};
export default withErrorBoundary(LozengeAction);